import React, { useState, useEffect } from 'react';
import { Loader2, X, User, Settings, Globe, CheckCircle2, Crown, CreditCard, ExternalLink, Sparkles, Sun, Moon, Monitor } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { TIMEZONE_OPTIONS, detectUserTimezone } from '../lib/timezones';
import { fetchCurrentProfile, upsertCurrentProfile } from '../services/profileService';
import { UserSettings } from '../types';
import { usePremium } from '../hooks/usePremium';
import { getCustomerPortalUrl } from '../services/subscriptionService';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProfileFormState {
  fullName: string;
  persona: string;
  timezone: string;
  bio: string;
}

const SETTINGS_KEY = 'msd_settings_v1';

type Tab = 'profile' | 'preferences' | 'general' | 'subscription';

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const { isPremium, isLoading: premiumLoading, subscription, aiTips, smartTasks, refetch: refetchPremium } = usePremium();

  // Settings State
  const [settings, setSettings] = useState<UserSettings>({ retentionHours: 24, workMinutes: 25, breakMinutes: 5 });
  const [saved, setSaved] = useState(false);

  // Profile State
  const [profileForm, setProfileForm] = useState<ProfileFormState>({
    fullName: '',
    persona: '',
    timezone: detectUserTimezone(),
    bio: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setSettings({
          retentionHours: parsed.retentionHours || 24,
          workMinutes: parsed.workMinutes || 25,
          breakMinutes: parsed.breakMinutes || 5
        });
      }
    } catch {
      /* ignore */
    }

    const loadProfile = async () => {
      setProfileLoading(true);
      setProfileError(null);
      setProfileSaved(false);
      try {
        const profile = await fetchCurrentProfile();
        setProfileForm({
          fullName: profile?.fullName ?? '',
          persona: profile?.persona ?? '',
          timezone: profile?.timezone ?? detectUserTimezone(),
          bio: profile?.bio ?? '',
        });
      } catch (err: unknown) {
        setProfileError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [isOpen]);

  const saveSettings = () => {
    const sanitized: UserSettings = {
      retentionHours: Math.min(168, Math.max(1, settings.retentionHours)),
      workMinutes: Math.min(90, Math.max(5, settings.workMinutes)),
      breakMinutes: Math.min(60, Math.max(1, settings.breakMinutes))
    };
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(sanitized));
    setSettings(sanitized);
    setSaved(true);
    window.dispatchEvent(new CustomEvent('msd-settings-updated'));
    setTimeout(() => setSaved(false), 2500);
  };

  const saveProfile = async () => {
    if (!profileForm.fullName.trim()) {
      setProfileError(t.profileMissingName);
      return;
    }
    setProfileSaving(true);
    setProfileError(null);
    setProfileSaved(false);
    try {
      await upsertCurrentProfile({
        fullName: profileForm.fullName,
        timezone: profileForm.timezone,
        persona: profileForm.persona || undefined,
        bio: profileForm.bio || undefined,
      });
      const refreshed = await fetchCurrentProfile();
      if (refreshed) {
        setProfileForm({
          fullName: refreshed.fullName ?? '',
          persona: refreshed.persona ?? '',
          timezone: refreshed.timezone ?? detectUserTimezone(),
          bio: refreshed.bio ?? '',
        });
      }
      setProfileSaved(true);
      window.dispatchEvent(
        new CustomEvent('msd-profile-updated', {
          detail: refreshed ?? null,
        }),
      );
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err: unknown) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'profile', label: language === 'ar' ? t.profileSectionTitle : 'Profile', icon: User },
    { id: 'preferences', label: language === 'ar' ? t.settingsStudyPreferences : 'Preferences', icon: Settings },
    // Subscription tab hidden during free-for-all testing mode
    // { id: 'subscription', label: language === 'ar' ? 'الاشتراك' : 'Subscription', icon: Crown },
    { id: 'general', label: language === 'ar' ? t.settingsLanguage : 'General', icon: Globe },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[80] p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-5xl sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col md:flex-row overflow-hidden h-[95vh] sm:h-[600px] md:h-[600px]">

        {/* Sidebar / Topbar */}
        <div className="w-full md:w-64 bg-slate-50 dark:bg-slate-950 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 flex flex-col flex-shrink-0">
          <div className="p-4 md:p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between md:block">
            <h2 className="text-lg md:text-xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
              {language === 'ar' ? 'الإعدادات' : 'Settings'}
            </h2>
            <button onClick={onClose} className="md:hidden p-2 text-slate-400 dark:text-slate-500">
              <X size={24} />
            </button>
          </div>

          <nav className="flex md:flex-col overflow-x-auto md:overflow-y-auto p-2 md:p-4 gap-2 md:space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as Tab)}
                  className={`flex-shrink-0 flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl transition-all duration-200 text-sm md:text-base whitespace-nowrap ${isActive
                    ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 font-semibold shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200 hover:shadow-sm'
                    }`}
                >
                  <Icon size={18} className="md:w-5 md:h-5" strokeWidth={isActive ? 2.5 : 2} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-900 min-h-0">
          {/* Header Mobile Only */}
          <div className="hidden md:flex p-4 justify-end">
            <button
              onClick={onClose}
              className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:px-8 md:pb-8 md:pt-2">

            {activeTab === 'profile' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">{t.profileSectionTitle}</h3>
                  <p className="text-slate-500 dark:text-slate-400">{t.profileSetupSubtitle}</p>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t.profileFullNameLabel}</label>
                      <input
                        type="text"
                        value={profileForm.fullName}
                        onChange={(e) => setProfileForm({ ...profileForm, fullName: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-400/10 transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
                        placeholder="Your Name"
                        disabled={profileLoading}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t.profilePersonaLabel}</label>
                      <input
                        type="text"
                        value={profileForm.persona}
                        onChange={(e) => setProfileForm({ ...profileForm, persona: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-400/10 transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
                        placeholder="Nickname"
                        disabled={profileLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t.profileTimezoneLabel}</label>
                    <select
                      value={profileForm.timezone}
                      onChange={(e) => setProfileForm({ ...profileForm, timezone: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-400/10 transition-all outline-none"
                      disabled={profileLoading}
                    >
                      {TIMEZONE_OPTIONS.map((zone) => (
                        <option key={zone.value} value={zone.value}>{zone.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t.profileBioLabel}</label>
                    <textarea
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-400/10 transition-all outline-none resize-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      rows={4}
                      disabled={profileLoading}
                    />
                  </div>
                </div>

                <div className="pt-4 flex items-center gap-4">
                  <button
                    onClick={saveProfile}
                    disabled={profileLoading || profileSaving}
                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold shadow-sm hover:shadow-md active:scale-95 transition-all text-sm flex items-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {t.profileSaveButton}
                  </button>
                  {profileSaved && <span className="text-green-600 dark:text-green-400 font-medium text-sm animate-in fade-in">{t.profileSaved}</span>}
                  {profileError && <span className="text-red-500 dark:text-red-400 font-medium text-sm animate-in fade-in">{profileError}</span>}
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">{language === 'ar' ? t.settingsStudyPreferences : 'Study Preferences'}</h3>
                  <p className="text-slate-500 dark:text-slate-400">Customize your focus timers and task management.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block">Work Interval</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={5}
                        max={90}
                        value={settings.workMinutes}
                        onChange={e => setSettings({ ...settings, workMinutes: Number(e.target.value) })}
                        className="w-20 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none text-center font-bold text-lg"
                      />
                      <span className="text-slate-500 dark:text-slate-400 text-sm">minutes</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block">Break Interval</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={60}
                        value={settings.breakMinutes}
                        onChange={e => setSettings({ ...settings, breakMinutes: Number(e.target.value) })}
                        className="w-20 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none text-center font-bold text-lg"
                      />
                      <span className="text-slate-500 dark:text-slate-400 text-sm">minutes</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block">Task Retention</label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">How long completed tasks stay visible before being archived.</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={168}
                        value={settings.retentionHours}
                        onChange={e => setSettings({ ...settings, retentionHours: Number(e.target.value) })}
                        className="w-20 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none text-center font-bold text-lg"
                      />
                      <span className="text-slate-500 dark:text-slate-400 text-sm">hours</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 flex items-center gap-4">
                  <button
                    onClick={saveSettings}
                    className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-black dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900 text-white font-semibold shadow-sm hover:shadow-md active:scale-95 transition-all text-sm flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {language === 'ar' ? 'حفظ التفضيلات' : 'Save Preferences'}
                  </button>
                  {saved && <span className="text-green-600 dark:text-green-400 font-medium text-sm animate-in fade-in">{language === 'ar' ? 'تم الحفظ ✓' : 'Saved ✓'}</span>}
                </div>
              </div>
            )}

            {activeTab === 'general' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">{language === 'ar' ? t.settingsLanguage : 'General Settings'}</h3>
                  <p className="text-slate-500 dark:text-slate-400">Language, theme, and application defaults.</p>
                </div>

                {/* Theme Toggle */}
                <div className="space-y-4">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block">
                    {language === 'ar' ? 'المظهر' : 'Appearance'}
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => { setTheme('light'); localStorage.setItem('triyo-theme', 'light'); }}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${theme === 'light'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400'
                        : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}
                    >
                      <Sun size={22} />
                      <span className="text-sm font-semibold">{language === 'ar' ? 'فاتح' : 'Light'}</span>
                    </button>

                    <button
                      onClick={() => { setTheme('dark'); localStorage.setItem('triyo-theme', 'dark'); }}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${theme === 'dark'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400'
                        : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}
                    >
                      <Moon size={22} />
                      <span className="text-sm font-semibold">{language === 'ar' ? 'داكن' : 'Dark'}</span>
                    </button>

                    <button
                      onClick={() => {
                        localStorage.removeItem('triyo-theme');
                        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                        setTheme(prefersDark ? 'dark' : 'light');
                      }}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${!localStorage.getItem('triyo-theme')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400'
                        : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}
                    >
                      <Monitor size={22} />
                      <span className="text-sm font-semibold">{language === 'ar' ? 'تلقائي' : 'System'}</span>
                    </button>
                  </div>
                </div>

                {/* Language */}
                <div className="space-y-4">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block">
                    {language === 'ar' ? 'لغة التطبيق' : 'Application Language'}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setLanguage('en')}
                      className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${language === 'en'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 text-blue-800 dark:text-blue-400'
                        : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}
                    >
                      <span className="text-2xl">🇺🇸</span>
                      <span className="font-semibold">English</span>
                    </button>

                    <button
                      onClick={() => setLanguage('ar')}
                      className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${language === 'ar'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 text-blue-800 dark:text-blue-400'
                        : 'border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}
                    >
                      <span className="text-2xl">🇸🇦</span>
                      <span className="font-semibold">العربية</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2 flex items-center gap-2">
                    <Crown className={`w-6 h-6 ${isPremium ? 'text-amber-500' : 'text-slate-400'}`} />
                    {language === 'ar' ? 'إدارة الاشتراك' : 'Subscription'}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {isPremium
                      ? (language === 'ar' ? 'أنت مشترك في الخطة المميزة!' : 'You have Premium access!')
                      : (language === 'ar' ? 'قم بالترقية لفتح جميع الميزات' : 'Upgrade to unlock all features')
                    }
                  </p>
                </div>

                {premiumLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Current Plan Card */}
                    <div className={`p-6 rounded-2xl border-2 ${isPremium ? 'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {isPremium ? (
                              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">PREMIUM</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-full">FREE</span>
                            )}
                            {subscription?.planType && (
                              <span className="text-sm text-slate-500 dark:text-slate-400 capitalize">{subscription.planType}</span>
                            )}
                          </div>
                          <h4 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                            {isPremium ? 'Triyo Premium' : 'Free Plan'}
                          </h4>
                          {subscription?.currentPeriodEnd && (
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {subscription.status === 'cancelled'
                                ? `Access until ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                                : `Renews ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                              }
                            </p>
                          )}
                        </div>
                        {isPremium && (
                          <Sparkles className="w-8 h-8 text-amber-400" />
                        )}
                      </div>
                    </div>

                    {/* Usage Stats (Free users) */}
                    {!isPremium && (
                      <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-4">
                        <h4 className="font-semibold text-slate-700 dark:text-slate-300">{language === 'ar' ? 'الاستخدام اليومي' : "Today's Usage"}</h4>

                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600 dark:text-slate-400">AI Study Tips</span>
                              <span className={`font-medium ${aiTips.remaining === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                {aiTips.remaining}/{aiTips.limit} remaining
                              </span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${aiTips.remaining === 0 ? 'bg-amber-500' : 'bg-blue-500'}`}
                                style={{ width: `${(aiTips.remaining / aiTips.limit) * 100}%` }}
                              />
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600 dark:text-slate-400">Smart Task Breakdowns</span>
                              <span className={`font-medium ${smartTasks.remaining === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                {smartTasks.remaining}/{smartTasks.limit} remaining
                              </span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${smartTasks.remaining === 0 ? 'bg-amber-500' : 'bg-blue-500'}`}
                                style={{ width: `${(smartTasks.remaining / smartTasks.limit) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>

                        <p className="text-xs text-slate-400 dark:text-slate-500">Limits reset daily at midnight</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      {!isPremium ? (
                        <button
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent("triyo:open-upgrade-modal"));
                          }}
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                        >
                          <Crown className="w-5 h-5" />
                          {language === 'ar' ? 'الترقية إلى Premium' : 'Upgrade to Premium'}
                        </button>
                      ) : (
                        <>
                          {subscription?.lemonCustomerId && (
                            <a
                              href={getCustomerPortalUrl(subscription.lemonCustomerId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 px-6 py-3 border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                            >
                              <CreditCard className="w-5 h-5" />
                              {language === 'ar' ? 'إدارة الفواتير' : 'Manage Billing'}
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => refetchPremium()}
                            className="px-4 py-3 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                          >
                            Refresh Status
                          </button>
                        </>
                      )}
                    </div>

                    {/* Premium Features List */}
                    {!isPremium && (
                      <div className="p-5 rounded-xl border border-dashed border-amber-200 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10 space-y-3">
                        <h4 className="font-semibold text-amber-800 dark:text-amber-400 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          {language === 'ar' ? 'ميزات Premium' : 'Premium Features'}
                        </h4>
                        <ul className="space-y-2 text-sm text-amber-700 dark:text-amber-300">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            Unlimited AI Study Tips
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            Unlimited Smart Task Breakdowns
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            30-day Analytics History
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            Unlimited Study Groups
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            Premium Pet Skins & Evolution
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                            Full Leaderboard Access
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};
